import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ChevronLeft, Video as VideoIcon } from "lucide-react";

export default function ExerciseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ex, setEx] = useState(null);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vidLoading, setVidLoading] = useState(true);

  useEffect(() => {
    api.get(`/exercises/${id}`).then(({ data }) => setEx(data)).finally(() => setLoading(false));
    api.get(`/exercises/${id}/video`).then(({ data }) => setVideo(data)).finally(() => setVidLoading(false));
  }, [id]);

  if (loading) return <AppShell title="Exercise"><Skeleton className="h-64 w-full rounded-2xl bg-card" /></AppShell>;
  if (!ex) return <AppShell title="Exercise"><p className="text-muted-foreground">Not found.</p></AppShell>;

  const sections = [
    ["Coaching cues", ex.coaching_cues],
    ["Common mistakes", ex.common_mistakes],
    ["Alternatives", ex.alternatives],
    ["Progressions", ex.progressions],
    ["Regressions", ex.regressions],
  ].filter(([, v]) => (v || []).length);

  return (
    <AppShell title="">
      <button onClick={() => navigate(-1)} className="flex items-center text-muted-foreground text-sm mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="space-y-4">
        <div>
          <h1 className="font-display text-2xl font-bold">{ex.name}</h1>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {ex.muscle_group && <Badge className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20">{ex.muscle_group}</Badge>}
            {ex.movement_pattern && <Badge className="bg-secondary border border-border text-muted-foreground">{ex.movement_pattern}</Badge>}
            {ex.equipment && <Badge className="bg-secondary border border-border text-muted-foreground">{ex.equipment}</Badge>}
            {ex.difficulty && <Badge className="bg-secondary border border-border text-muted-foreground">{ex.difficulty}</Badge>}
          </div>
        </div>

        {ex.description && (
          <Card className="rounded-2xl bg-card border-border p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">How to perform</p>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{ex.description}</p>
          </Card>
        )}

        <Card className="rounded-2xl bg-card border-border p-2">
          {vidLoading ? (
            <Skeleton className="w-full rounded-xl bg-card" style={{ aspectRatio: "16/9" }} />
          ) : video && video.embed_url ? (
            <div data-testid="exercise-video-player">
              <AspectRatio ratio={16 / 9}>
                <iframe
                  title={ex.name}
                  src={video.embed_url}
                  className="w-full h-full rounded-xl"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
              <div className="px-2 py-2">
                <p className="text-sm font-medium line-clamp-1">{video.video_title}</p>
                <p className="text-xs text-muted-foreground">{video.channel}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <VideoIcon className="h-8 w-8 mb-2" />
              <p className="text-sm">No video available yet.</p>
            </div>
          )}
        </Card>

        {sections.length > 0 && (
          <Accordion type="single" collapsible defaultValue="item-0" className="space-y-2">
            {sections.map(([title, items], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-border rounded-xl px-4 bg-card">
                <AccordionTrigger className="text-sm font-semibold hover:no-underline">{title}</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-1.5 pb-2">
                    {items.map((it, j) => (
                      <li key={j} className="text-sm text-muted-foreground flex gap-2"><span className="text-[#ccff0c]">•</span>{it}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </AppShell>
  );
}
